import { Component, inject, signal, OnInit, Input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InventoryService } from '../../../../../shared/services/inventory.service';
import { DialogRef } from '../../../../../core/services/dialog.service';
import { AppInput } from '../../../../../shared/components/app-input/app-input';
import { AppButton } from '../../../../../shared/components/app-button/app-button';
import { firstValueFrom } from 'rxjs';
import { Product, ManualTransactionDto, Supplier } from '../../../../../shared/models/inventory.models';
import { ToastService } from '../../../../../core/services/toast.service';
import { DialogService } from '../../../../../core/services/dialog.service';
import { SupplierFormDialog } from '../supplier-form-dialog/supplier-form-dialog';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

@Component({
  selector: 'app-inventory-transaction-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, AppInput, AppButton],
  templateUrl: './transaction-dialog.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class InventoryTransactionDialog implements OnInit {
  private inventoryService = inject(InventoryService);
  private dialogRef = inject(DialogRef);
  private dialogService = inject(DialogService);
  private toast = inject(ToastService);

  @Input() product?: Product;
  type = signal<'IN' | 'OUT' | 'ADJUSTMENT'>('IN');

  quantity = signal<number>(0);
  notes = signal<string>('');

  // Nuevos campos para Prop
  lotNumber = signal<string>('');
  expiryDate = signal<string>('');
  supplierId = signal<string>('');
  invoiceNumber = signal<string>('');
  purchasePrice = signal<number>(0);

  suppliers = signal<Supplier[]>([]);
  availableLots = signal<any[]>([]);
  lotSearchTerm = signal<string>('');
  isAutoDeduct = signal<boolean>(true); // Por defecto en salidas

  // Computed para filtrar lotes
  filteredLots = computed(() => {
    const term = this.lotSearchTerm().toLowerCase();
    if (!term) return this.availableLots();
    return this.availableLots().filter(l =>
      (l.lotNumber || '').toLowerCase().includes(term) ||
      (l.expiryDate ? new Date(l.expiryDate).toLocaleDateString() : '').includes(term)
    );
  });

  selectedLot = signal<any | null>(null);
  loading = signal(false);

  types: { label: string, value: 'IN' | 'OUT' | 'ADJUSTMENT' }[] = [
    { label: 'Entrada (Compra/Donación)', value: 'IN' },
    { label: 'Salida (Merma/Vencimiento)', value: 'OUT' },
    { label: 'Ajuste Manual (Inventario Físico)', value: 'ADJUSTMENT' }
  ];

  async ngOnInit() {
    this.loadSuppliers();
    if (this.type() !== 'IN') {
      this.loadLots();
    }
  }

  async loadLots() {
    if (!this.product?._id) return;
    try {
      const lots = await firstValueFrom(this.inventoryService.getProductStocks(this.product._id));
      this.availableLots.set(lots);
    } catch (err) {
      console.error('Error cargando lotes', err);
    }
  }

  onTypeChange(val: 'IN' | 'OUT' | 'ADJUSTMENT') {
    this.type.set(val);
    this.isAutoDeduct.set(val === 'OUT'); // Auto-habilitar en salidas
    if (val !== 'IN') {
      this.loadLots();
    } else {
      // Reset lot fields if switching to IN
      this.lotNumber.set('');
      this.expiryDate.set('');
      this.selectedLot.set(null);
    }
  }

  onLotSelect(lot: any) {
    this.selectedLot.set(lot);
    if (lot) {
      this.lotNumber.set(lot.lotNumber || '');
      this.expiryDate.set(lot.expiryDate ? new Date(lot.expiryDate).toISOString().split('T')[0] : '');
      // If OUT, we might want to suggest the whole lot quantity or let the user decide
    }
  }

  async loadSuppliers() {
    try {
      const data = await firstValueFrom(this.inventoryService.getSuppliers());
      this.suppliers.set(data);
    } catch (err) {
      console.error('Error cargando proveedores', err);
    }
  }

  async openSupplierForm() {
    const dialog = this.dialogService.open(SupplierFormDialog, {
      title: 'Nuevo Proveedor',
      width: '500px'
    });

    const result = await firstValueFrom(dialog.afterClosed());
    if (result && (result as any)._id) {
      await this.loadSuppliers();
      this.supplierId.set((result as any)._id);
    }
  }

  async save() {
    if (!this.product) return;

    if (this.quantity() <= 0 && this.type() !== 'ADJUSTMENT') {
      this.toast.show('Error', 'La cantidad debe ser mayor a 0', 'error');
      return;
    }

    this.loading.set(true);
    try {
      const dto: ManualTransactionDto = {
        productId: this.product._id,
        warehouseId: undefined,
        type: this.type(),
        quantity: this.quantity(),
        notes: this.notes(),
        lotNumber: this.isAutoDeduct() ? '' : this.lotNumber(),
        expiryDate: this.isAutoDeduct() ? undefined : (this.expiryDate() ? new Date(this.expiryDate()) : undefined),
        supplierId: this.supplierId() || undefined,
        invoiceNumber: this.invoiceNumber(),
        purchasePrice: this.type() === 'IN' ? this.purchasePrice() : undefined,
        isAutoDeduct: this.isAutoDeduct()
      };

      // Note: For now we'll let the backend use the default warehouse as implemented in InventoryService.ensureMainWarehouse
      // But if needed we can fetch warehouse ID here.

      await firstValueFrom(this.inventoryService.executeManualTransaction(dto));
      this.toast.show('Éxito', 'Movimiento registrado correctamente', 'success');
      this.dialogRef.close(true);
    } catch (e: any) {
      this.toast.show('Error', e.error?.message || 'Error al registrar movimiento', 'error');
    } finally {
      this.loading.set(false);
    }
  }

  close() {
    this.dialogRef.close();
  }
}
